import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { DEFAULT_TIMEZONE, getTimezoneList } from './constants';
import type { UploadOptions } from './types';

interface ConfirmUploadDialogProps {
  open: boolean;
  fileName: string;
  onConfirm: (options: UploadOptions) => void;
  onCancel: () => void;
}

const ConfirmUploadDialog: React.FC<ConfirmUploadDialogProps> = ({
  open,
  fileName,
  onConfirm,
  onCancel,
}) => {
  const defaultName = fileName.replace(/\.(csv|tsv)$/i, '');
  const [uploadName, setUploadName] = useState(defaultName);
  const [uploadType, setUploadType] = useState<'upload_v3' | 'delete'>('upload_v3');
  const [processMode, setProcessMode] = useState<'immediately' | 'schedule'>('immediately');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);

  const timezones = useMemo(() => getTimezoneList(), []);

  // Reset form state when dialog opens with a new file
  React.useEffect(() => {
    if (open) {
      const name = fileName.replace(/\.(csv|tsv)$/i, '');
      setUploadName(name);
      setUploadType('upload_v3');
      setProcessMode('immediately');
      setScheduleDate('');
      setScheduleTime('');
      setTimezone(DEFAULT_TIMEZONE);
    }
  }, [open, fileName]);

  const isScheduleValid = (): boolean => {
    if (processMode === 'immediately') return true;
    if (!scheduleDate || !scheduleTime) return false;

    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
    return scheduledDateTime > new Date();
  };

  const isFormValid = uploadName.trim().length > 0 && isScheduleValid();

  const handleConfirm = () => {
    const options: UploadOptions = {
      uploadName: uploadName.trim(),
      uploadType,
    };

    if (processMode === 'schedule') {
      options.scheduleAt = `${scheduleDate}T${scheduleTime}:00`;
      options.timezone = timezone;
    }

    onConfirm(options);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Upload Options</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Upload Name
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A short name or description for this upload. Defaults to the file name.
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={uploadName}
          onChange={e => setUploadName(e.target.value)}
          sx={{ mb: 3 }}
        />

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">
            <Typography variant="subtitle2">Upload Type</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose how this file should be applied to the catalog.
            </Typography>
          </FormLabel>
          <RadioGroup
            value={uploadType}
            onChange={e => setUploadType(e.target.value as 'upload_v3' | 'delete')}
          >
            <FormControlLabel
              value="upload_v3"
              control={<Radio />}
              label="Create or Modify Records"
            />
            <FormControlLabel
              value="delete"
              control={<Radio />}
              label="Delete Records"
            />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset">
          <FormLabel component="legend">
            <Typography variant="subtitle2">When to Process</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose whether to process this upload immediately or schedule it for later.
            </Typography>
          </FormLabel>
          <RadioGroup
            value={processMode}
            onChange={e => setProcessMode(e.target.value as 'immediately' | 'schedule')}
          >
            <FormControlLabel
              value="immediately"
              control={<Radio />}
              label="Process immediately"
            />
            <FormControlLabel
              value="schedule"
              control={<Radio />}
              label="Schedule for later"
            />
          </RadioGroup>
        </FormControl>

        {processMode === 'schedule' && (
          <>
            <TextField
              label="Date"
              type="date"
              size="small"
              fullWidth
              value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              label="Time"
              type="time"
              size="small"
              fullWidth
              value={scheduleTime}
              onChange={e => setScheduleTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              options={timezones}
              value={timezone}
              onChange={(_, value) => setTimezone(value ?? DEFAULT_TIMEZONE)}
              renderInput={params => (
                <TextField {...params} label="Timezone" size="small" />
              )}
              disableClearable
              size="small"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isFormValid}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmUploadDialog;
